import { config } from '../config';
import type {
  BuildResponse,
  CatalogItem,
  KingdomCollection,
  Me,
  MyKingdom,
  PlotCollection,
  StructureCollection,
  WorldStats,
} from './models';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// getJson fetches and decodes a JSON response, throwing ApiError on a non-2xx.
// The optional bearer token is the seam the later auth slice fills in for the
// login-gated /v1 detail endpoints; the public read routes pass none.
async function getJson<T>(path: string, token?: string): Promise<T> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${config.apiBase}${path}`, { headers });
  if (!res.ok) {
    throw new ApiError(res.status, `GET ${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

// sendJson performs a (possibly body-carrying) JSON request. Unlike getJson
// it surfaces the server's {"error": "..."} message verbatim in ApiError —
// the admin UI shows those to the operator (e.g. 409 floor violations).
export async function sendJson<T>(
  method: string,
  path: string,
  opts: { body?: unknown; token?: string } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
  let body: string | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(`${config.apiBase}${path}`, { method, headers, body });
  if (!res.ok) {
    let message = `${method} ${path} failed: ${res.status}`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      // Non-JSON error body — keep the generic message.
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  publicKingdoms(bbox?: string): Promise<KingdomCollection> {
    const query = bbox ? `?bbox=${encodeURIComponent(bbox)}` : '';
    return getJson<KingdomCollection>(`/public/kingdoms${query}`);
  },
  publicStructures(bbox?: string): Promise<StructureCollection> {
    const query = bbox ? `?bbox=${encodeURIComponent(bbox)}` : '';
    return getJson<StructureCollection>(`/public/structures${query}`);
  },
  publicStats(): Promise<WorldStats> {
    return getJson<WorldStats>('/public/stats');
  },
  // The signed-in player's own record; 404 means the auth user has no player
  // row (a Google identity that was never linked from the mobile app).
  me(token: string): Promise<Me> {
    return getJson<Me>('/v1/players/me', token);
  },
  // The signed-in player's kingdom, or null when they have none (404).
  async myKingdom(token: string): Promise<MyKingdom | null> {
    try {
      return await getJson<MyKingdom>('/v1/kingdoms/mine', token);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  kingdomPlots(token: string, kingdomId: string): Promise<PlotCollection> {
    return getJson<PlotCollection>(
      `/v1/kingdoms/${encodeURIComponent(kingdomId)}/plots`,
      token,
    );
  },
  async structureCatalog(token: string): Promise<CatalogItem[]> {
    const res = await getJson<{ items: CatalogItem[] }>(
      '/v1/structures/catalog',
      token,
    );
    return res.items;
  },
  buildStructure(
    token: string,
    body: { building_id: string; type: string },
  ): Promise<BuildResponse> {
    return sendJson<BuildResponse>('POST', '/v1/structures', { body, token });
  },
};
